import React from 'react';

// Shared package-row building blocks used by the Collection Panel, the
// customer profile Vouchers tab and the bulk Cancel Voucher screen.
//
// Presentational only: no API calls, no state, no business rules. Every
// status, stock and eligibility decision belongs to the server. All values are
// rendered as React text children, never as markup.

// Package rows and voucher rows are told apart by this explicit row type and
// never by an id: purchase ids and reservation ids are different id spaces
// that collide.
export const PACKAGE_ROW_TYPE = 'package';

const BLANK = '—';

export const isPackageRow = (rowOriginal) =>
  rowOriginal?.rowType === PACKAGE_ROW_TYPE;

// Title cell for a package row: the package name followed by a pill marking it
// a package. The expand chevron is injected by PaginationTable, not here.
export const PackageTitleCell = ({ row }) => {
  const count = Number(row?.voucherCount) || 0;
  const noun = count === 1 ? 'voucher' : 'vouchers';
  return (
    <span className='d-inline-flex align-items-center gap-2'>
      <span>{row?.title}</span>
      <span className='bdg gold'>{`Bundle · ${count} ${noun}`}</span>
    </span>
  );
};

// Expanded sub-row body: one entry per member voucher. A member's serialNumber
// arrives null from the server until that member is collected, and renders as
// a blank placeholder until then.
export const PackageMembers = ({ vouchers }) => {
  const members = Array.isArray(vouchers) ? vouchers : [];
  return (
    <table className='table table-sm mb-0'>
      <thead>
        <tr>
          <th scope='col'>Store</th>
          <th scope='col'>Voucher</th>
          <th scope='col'>Collection Code</th>
          <th scope='col'>Serial Number</th>
        </tr>
      </thead>
      <tbody>
        {members.map((member) => (
          <tr key={member.reserveVoucherId}>
            <td>{member.storeName}</td>
            <td>{member.voucherTitle}</td>
            <td>{member.collectionCode}</td>
            <td>{member.serialNumber ?? BLANK}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const TITLE_ACCESSORS = ['title', 'voucherTitle'];
const BLANK_FOR_PACKAGE_ACCESSORS = ['startDate', 'expiryDate'];

// Run the column's original Cell (or the default value render) so a voucher
// row, and a package row's reused value, format exactly as before.
const renderOriginal = (column, props, value) =>
  column.Cell
    ? column.Cell({ ...props, value })
    : value === undefined || value === null
      ? ''
      : value;

// Takes a column array written for single voucher rows and returns a NEW array
// in which the affected columns branch on isPackageRow(row.original). Headers,
// ids and accessors are preserved exactly because PaginationTable's column-id
// switch matches on them. The input array and its column objects are never
// mutated: they are module-level singletons shared with other screens.
//
// The caller MUST memoise the result (useMemo): PaginationTable memoises on the
// columns array identity, so a fresh array every render causes a refetch loop.
export const makePackageAwareColumns = (columns) =>
  columns.map((column) => {
    const { accessor } = column;

    if (TITLE_ACCESSORS.includes(accessor)) {
      return {
        ...column,
        Cell: (props) =>
          isPackageRow(props.row?.original) ? (
            <PackageTitleCell row={props.row.original} />
          ) : (
            renderOriginal(column, props, props.value)
          ),
      };
    }

    if (accessor === 'reserveDate') {
      return {
        ...column,
        Cell: (props) =>
          renderOriginal(
            column,
            props,
            isPackageRow(props.row?.original)
              ? props.row.original.createdAt
              : props.value
          ),
      };
    }

    if (BLANK_FOR_PACKAGE_ACCESSORS.includes(accessor)) {
      return {
        ...column,
        Cell: (props) =>
          isPackageRow(props.row?.original)
            ? BLANK
            : renderOriginal(column, props, props.value),
      };
    }

    return column;
  });
